import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  HostListener,
  Inject
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { PaginatorModule } from 'primeng/paginator';
import { NgbPaginationModule } from '@ng-bootstrap/ng-bootstrap';
import { DropdownModule } from 'primeng/dropdown';
import { InputIconModule } from 'primeng/inputicon';
import { IconFieldModule } from 'primeng/iconfield';
import { TranslateModule } from '@ngx-translate/core';
import { Item } from '../../interfaces/shared-interfaces';
import { NoDataMessageComponent } from '../no-data-message/no-data-message.component';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { SkeletonModule } from 'primeng/skeleton';
import { Logger } from '../../../../common/core/utilities/logging/logger';
import { FormInputConfig } from '../../interfaces/form-input-config';
import { FilterComponent } from '../../../../common/core/ui/filters/filter/filter.component';
import { ToggleButtonChangeEvent, ToggleButtonModule } from 'primeng/togglebutton';

// Define the interface for the table data structure

@Component({
  selector: 'app-shared-table',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    FormsModule,
    PaginatorModule,
    NgbPaginationModule,
    SkeletonModule,
    DropdownModule,
    InputIconModule,
    IconFieldModule,
    TranslateModule,
    NoDataMessageComponent,
    FilterComponent,
    ToggleButtonModule
  ],
  templateUrl: './shared-table.component.html',
  styleUrls: ['./shared-table.component.scss'], // Use styleUrls for correct styling
  providers: [DatePipe]
})
export class SharedTableComponent implements OnInit {
  @Input() items!: Item;
  @Input() enablePagination: boolean = true;
  @Input() enableSorting: boolean = true;
  @Input() isLoading: boolean = false; // New Input for loading state in the body
  @Input() hasHeader: boolean = true;
  @Input() hasSearchInp: boolean = true;
  @Input() tableHeader: string = '';

  @Input() initialPageSize: number = 1;
  @Input() totalRecords: number = 0;
  @Input() customTemplate: boolean = false;
  @Input() bodyTemplate!: TemplateRef<any>;
  @Input() actionsTemplate!: TemplateRef<any>;
  @Input() supportedActions: any[] = [];
  @Input() buttonText!: string;
  @Input() showBtn: boolean = true;

  @Input() showToggleFillter: boolean = false;
  @Input() toggleFillterText: string = 'Toggle Fillter';
  @Output() toggleClick = new EventEmitter<boolean>();
  toggleFilterTextOn: string = 'GENERAL.ON'; // Text when activated
  toggleFilterTextOff: string = 'GENERAL.OFF'; // Text when deactivated
  isFilterActive: boolean = false; // Tracks the current toggle state

  @Input() showOtherActionBtn: boolean = false;
  @Input() otherActionBtnText: string = 'Action';
  @Input() HandelRenderingDataMessage: string = 'Loading...';
  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() search = new EventEmitter<string>();
  @Output() buttonClick = new EventEmitter<void>();
  @Output() otherActionButtonClick = new EventEmitter<void>();

  @Input() showFilterBtn: boolean = false;
  @Input() sidebarVisible: boolean = false;
  @Input() filterOptions: FormInputConfig[] = [];
  @Output() sidebarVisibleChange = new EventEmitter<boolean>();
  @Output() applyFilter = new EventEmitter<any>();
  @Output() clearFilter = new EventEmitter<void>();

  filteredItems: Item['data'][] = [];
  pageSizesPaginations: number[] = [5, 10, 20];
  searchTerm: string = '';
  @Input() page = 1;
  @Input() size = this.initialPageSize;
  pageSize = this.initialPageSize;
  // Subject for search input debounce
  private searchSubject = new Subject<string>();
  isMobile = false;
  private destroy$ = new Subject<void>();

  constructor(
    public cdr: ChangeDetectorRef,
    @Inject(DatePipe) private datePipe: DatePipe
  ) {
    this.checkScreenSize();
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.checkScreenSize();
  }

  private checkScreenSize() {
    this.isMobile = window.innerWidth < 992;
  }

  // Lifecycle hook that runs on component initialization
  ngOnInit(): void {
    // Debounce the search input
    this.searchSubject
      .pipe(
        debounceTime(400) // Emit value after Xms of no changes
      )
      .subscribe((searchTerm) => {
        this.search.emit(searchTerm);
      });
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.totalRecords = this.totalRecords;
    this.size = this.initialPageSize;
    this.pageSize = this.initialPageSize;
  }

  // Method to apply search filtering logic
  applySearchFilter(searchTerm: string): void {
    if (this.items?.data) {
      this.filteredItems = this.items.data.filter((item: any) =>
        Object.values(item).some((value: any) => value?.toString().toLowerCase().includes(searchTerm.toLowerCase()))
      );
    } else {
      this.filteredItems = [];
    }
  }

  // Method to handle search functionality
  onSearch(): void {
    this.page = 1;
    this.searchSubject.next(this.searchTerm); // Emit search term to the Subject
  }

  // Method to handle button click
  onButtonClick(): void {
    this.buttonClick.emit(); // Emit the button click event
  }

  // Method to handle other action button click
  onOtherActionButtonClick(): void {
    this.otherActionButtonClick.emit(); // Emit the button click event
  }

  // Method to handle other toggle button click
  onToggleFilterChange(event: ToggleButtonChangeEvent | any): void {
    this.isFilterActive = event.checked;
    Logger.debug('Filter Event:', event);
    Logger.debug('Filter toggled:', this.isFilterActive);
    // Emit or handle filter state change logic here
    this.toggleClick.emit(this.isFilterActive); // Emit the button click event
  }

  // Method to handle page size changes
  onPageSizeChange(newPageSize: any): void {
    this.size = newPageSize.value; // Update page size
    // this.page = 1; // Reset to the first page when page size changes
    this.pageSizeChange.emit(this.size); // Emit new page size
    Logger.debug(newPageSize.value); // Log the new page size for debugging
  }

  // Method to handle page changes
  onPageChange(newPage: number): void {
    this.page = newPage; // Update current page
    this.pageChange.emit(this.page); // Emit new page number
  }

  openSidebar() {
    this.sidebarVisible = true;
  }

  onSidebarVisibleChange(visible: boolean) {
    this.sidebarVisible = visible;
    this.sidebarVisibleChange.emit(visible);
  }

  onApplyFilter(filters: any) {
    this.applyFilter.emit(filters);
  }

  onClearFilter() {
    this.clearFilter.emit();
  }

  getNestedValue(obj: any, key: string): any {
    if (key.startsWith('element.')) {
      key = key.replace(/^element\./, '');
    }
    return key.split('.').reduce((x, y) => x && x[y], obj);
  }

  formatValue(key: string, value: any): any {
    if (key === 'createdOnUtc' || key === 'createdAt' || key === 'modifiedOnUtc') {
      return this.datePipe.transform(value, 'dd/MM/yyyy');
    }
    if (key === 'isActive') {
      return value; // boolean, handled in template
    }
    if (key === 'status') {
      return value; // string, handled in template
    }
    return value;
  }
}
